class FilterRemove extends HTMLElement {
  connectedCallback() {
    this.link = this.querySelector('a');
    if(this.link) {
      this.link.setAttribute('role', 'button');
      this.link.addEventListener('click', this.removeFilter.bind(this));
      this.link.addEventListener('keyup', (event) => {
        event.preventDefault();
        if(event.code.toUpperCase() === 'SPACE') this.removeFilter(event);
      });
    }
  }

  removeFilter(event) {
    event?.preventDefault();
    const form = this.closest('catalog-filters') || document.querySelector('catalog-filters');
    form.onActiveFilterClick(event);
  }
}

customElements.define('filter-remove', FilterRemove);

class PriceRange extends HTMLElement {
  connectedCallback() {
    this.inputs = this.querySelectorAll('input');
    this.inputs.forEach(element => element.addEventListener('change', this.onRangeChange.bind(this)));
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const minInput = this.inputs[0];
    const maxInput = this.inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max=  Number(input.getAttribute('max'));

    if(value < min) input.value = min;
    if(value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FilterShowMore extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.toggle);
    this.moreLabel = this.dataset.moreLabel;
    this.lessLabel = this.dataset.lessLabel;
  }

  toggle(event) {
    event && event.preventDefault();
    if(this.dataset.state === 'open') {
      this.close();
    }
    else {
      this.open();
    }
  }

  close() {
    this.querySelector('.icon').textContent = '+';
    this.querySelector('.label').textContent = this.moreLabel
    this.setAttribute('data-state', 'closed');
    this.closest('fieldset').querySelectorAll('.show-more-item')
      .forEach(field => field.classList.add('hidden'));
  }

  open() {
    this.querySelector('.icon').textContent = '-';
    this.querySelector('.label').textContent = this.lessLabel
    this.setAttribute('data-state', 'open');
    this.closest('fieldset').querySelectorAll('.show-more-item')
      .forEach(field => field.classList.remove('hidden'));
  }
}

customElements.define('filter-show-more', FilterShowMore);

class CatalogFilters extends HTMLElement {
  connectedCallback() {
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);
    this.forms = document.querySelectorAll(`form[data-form-id="${this.dataset.formId}"]`);
    this.forms.forEach(form => form.addEventListener('input', this.debouncedOnSubmit.bind(this)))
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : CatalogFilters.searchParamsInitial;
      if (searchParams === CatalogFilters.searchParamsPrev) return;
      CatalogFilters.renderPage(searchParams, null, false);
    }
    window.addEventListener('popstate', onHistoryChange);
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    CatalogFilters.searchParamsPrev = searchParams;
    const sections = CatalogFilters.getSections();
    CatalogFilters.enableGridLoading();

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = element => element.url === url;

      CatalogFilters.filterData.some(filterDataUrl)
        ? CatalogFilters.renderSectionFromCache(filterDataUrl, event)
        : CatalogFilters.renderSectionFromFetch(url, event);
    });

    if(updateURLHash) {
      CatalogFilters.updateURLHash(searchParams);
    }
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then(response => response.text())
      .then((responseText) => {
        const html = responseText;
        CatalogFilters.filterData = [...CatalogFilters.filterData, {html, url}];
        CatalogFilters.renderFilters(html, event);
        CatalogFilters.renderProductGridContainer(html);
        CatalogFilters.renderProductCount(html);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = CatalogFilters.filterData.find(filterDataUrl).html;
    CatalogFilters.renderFilters(html, event);
    CatalogFilters.renderProductGridContainer(html);
    CatalogFilters.renderProductCount(html);
  }

  static renderProductGridContainer(html) {
    document.getElementById('CatalogGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('CatalogGridContainer')
      .innerHTML;
    CatalogFilters.disableGridLoading();
  }

  static renderProductCount(html) {
    const count = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductCountDesktop')
      .innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    const containerMobile = document.getElementById('ProductCountMobile');
    container.innerHTML = count;
    if(containerDesktop) {
      containerDesktop.innerHTML = count;
    }
    if(containerMobile) {
      containerMobile.innerHTML = count;
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const detailsElement = parsedHTML
      .querySelectorAll('catalog-filters .js-filter, catalog-filters-drawer .js-filter');
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    }
    const toRender = Array.from(detailsElement)
      .filter(element => !matchesIndex(element));
    const countsToRender = Array.from(detailsElement)
      .find(matchesIndex);
    toRender.forEach((element) => {
      document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`).innerHTML = element.innerHTML;
    });
    CatalogFilters.renderActiveFilters(parsedHTML);

    if (countsToRender) CatalogFilters.renderCounts(countsToRender, event.target.closest('.js-filter'));
  }

  static renderActiveFilters(html) {
    const activeFilters = ['.active-filters-mobile', '.active-filters-desktop'];

    activeFilters.forEach((selector) => {
      const activeFilter = html.querySelector(selector);
      if (!activeFilter) return;
      document.querySelector(selector).innerHTML = activeFilter.innerHTML;
    });
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.filters__selected');
    const sourceElement = source.querySelector('.filters__selected');

    if (sourceElement && targetElement) {
      target.querySelector('.filters__selected').outerHTML = source.querySelector('.filters__selected').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('catalog-grid').dataset.id,
      }
    ]
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    CatalogFilters.renderPage(searchParams, event);
  }

  static enableGridLoading() {
    document.getElementById('catalog-grid').setAttribute('data-state', 'loading');
  }

  static disableGridLoading() {
    document.getElementById('catalog-grid').setAttribute('data-state', 'waiting');
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('form.filter-form');
    const forms = [];

    sortFilterForms.forEach((form) => {
      if(form.dataset.formId != this.dataset.formId) {
        if(event.target.getAttribute('type') === 'checkbox') {
          const hasField = form.querySelector(`[name="${event.target.getAttribute('name')}"][value="${event.target.value}"]`);
          if(hasField) {
            hasField.checked = event.target.checked;
          }
        }
      }
      forms.push(...this.createSearchParams(form).split('&'));
    });
    this.onSubmitForm([...new Set(forms)].filter(value => value?.length > 0).join('&'), event)
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    CatalogFilters.renderPage(url);
  }
}

CatalogFilters.filterData = [];
CatalogFilters.searchParamsInitial = window.location.search.slice(1);
CatalogFilters.searchParamsPrev = window.location.search.slice(1);
customElements.define('catalog-filters', CatalogFilters);
CatalogFilters.setListeners();